import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCalendarCheckOutline,
  mdiCalendarClockOutline,
  mdiCheckBold,
  mdiClipboardTextClockOutline,
  mdiClockOutline,
  mdiCloseCircleOutline,
  mdiHandshakeOutline,
} from "@mdi/js"
import {
  contactOrderStepStatusInfo,
  buildContactOrderClosureSubLabel,
  contactOrderClosedActorLabel,
  contactOrderClosedReasonDescription,
  contactOrderClosedReasonLabel,
  displayContactChannelLabel,
  displayContactOrderStepStatusLabel,
  displayContactStepResultStatusLabel,
  isActiveContactOrder,
  type WorkOrderViewStatus,
} from "../outreach-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipList,
  tooltipText,
  tooltipTitle,
} from "../rich-tooltips"

export type AnnouncementStatusPresentation = {
  definition: StatusDefinition<string>
  label: string
  subLabel: string | null
  tooltip: string | null
}

const parseDate = (value?: string | null) => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDateTime = (value?: string | null) => {
  const parsed = parseDate(value)

  if (!parsed) return "–"

  return `${parsed.toLocaleDateString("de-DE")} ${parsed.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

const formatShortDateTime = (value?: string | null) => {
  const parsed = parseDate(value)

  if (!parsed) return null

  return `${parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  })} ${parsed.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

const eventAgreementTimestamp = (event?: ScheduleEvent | null) => {
  const eventWithConfirmation = event as
    | {
        confirmed_at?: string | null
        lifecycle_summary?: { confirmed_at?: string | null } | null
        appointment_contact?: { confirmed_at?: string | null } | null
      }
    | null
    | undefined

  return (
    eventWithConfirmation?.confirmed_at ??
    eventWithConfirmation?.appointment_contact?.confirmed_at ??
    eventWithConfirmation?.lifecycle_summary?.confirmed_at ??
    null
  )
}

const normalizeStatus = (value?: string | null) => `${value ?? ""}`.trim().toUpperCase()
const normalizeId = (value?: unknown | null) => `${value ?? ""}`.trim()

const unsuccessfulContactResults = new Set([
  "NO_RESPONSE",
  "NOT_REACHED",
  "INVALID_CONTACT",
  "APPOINTMENT_DECLINED",
])

const isClosedContactOrder = (order?: ContactOrder | null) =>
  Boolean(order?.id && !isActiveContactOrder(order))

const buildStatusTooltip = ({
  details,
  appointmentContactLabel,
  fields = [],
}: {
  title: string
  status?: string | null
  details?: string | null
  appointmentContactLabel?: string | null
  fields?: Array<[string, unknown]>
  steps?: ContactOrderStep[]
}) =>
  buildRichTooltip([
    tooltipText(details),
    tooltipField("Ansprechpartner", appointmentContactLabel),
    ...fields.map(([label, value]) => tooltipField(label, value)),
  ])

const isPendingPlanStep = (step?: ContactOrderStep | null) => {
  if (!step) return false

  const status = normalizeStatus(step.status)

  return !["DONE", "CANCELED", "ERROR"].includes(status)
}

const toTimestamp = (value?: string | null) => {
  if (!value) return Infinity

  const parsed = new Date(value)
  const ts = parsed.getTime()

  return Number.isFinite(ts) ? ts : Infinity
}

const pickEarlierStep = (current: ContactOrderStep | undefined, candidate: ContactOrderStep) => {
  if (!current) return candidate

  const currentTs = toTimestamp(current.due_at)
  const candidateTs = toTimestamp(candidate.due_at)

  if (currentTs !== candidateTs) return candidateTs < currentTs ? candidate : current

  return (candidate.position ?? 0) < (current.position ?? 0) ? candidate : current
}

export const pickNextAnnouncementStep = (steps: ContactOrderStep[] = []) =>
  steps
    .filter(isPendingPlanStep)
    .slice()
    .sort((a, b) => {
      const aTs = toTimestamp(a.due_at)
      const bTs = toTimestamp(b.due_at)

      if (aTs !== bTs) return aTs - bTs

      return (a.position ?? 0) - (b.position ?? 0)
    })[0] ?? null

export const deriveNextPlanStepByWorkOrderId = (contactOrders: ContactOrder[]) => {
  const nextStepByOrderId: Record<string, ContactOrderStep> = {}
  contactOrders.forEach((order) => {
    if (!order?.work_order_id) return

    if (!order.steps?.length) return

    if (!isActiveContactOrder(order)) return

    const next = pickNextAnnouncementStep(order.steps)

    if (!next) return

    const existing = nextStepByOrderId[order.work_order_id]
    nextStepByOrderId[order.work_order_id] = pickEarlierStep(existing, next)
  })

  return nextStepByOrderId
}

const stepDisplayTimestamp = (step?: ContactOrderStep | null) => {
  if (!step) return null

  const terminal = ["DONE", "CANCELED", "ERROR"].includes(normalizeStatus(step.status))

  if (terminal) {
    return (
      step.result_recorded_at ??
      step.due_at ??
      step.next_action_at ??
      step.response_deadline_at ??
      null
    )
  }

  return (
    step.next_action_at ??
    step.response_deadline_at ??
    step.due_at ??
    step.result_recorded_at ??
    null
  )
}

const stepTimelineTimestamp = (step?: ContactOrderStep | null) => {
  const raw = stepDisplayTimestamp(step)

  if (!raw) return null

  const parsed = new Date(raw)
  const ts = parsed.getTime()

  return Number.isFinite(ts) ? ts : null
}

const pickLatestStep = (steps: ContactOrderStep[] = []) =>
  steps.slice().sort((a, b) => {
    const aTs = stepTimelineTimestamp(a) ?? -Infinity
    const bTs = stepTimelineTimestamp(b) ?? -Infinity

    if (aTs !== bTs) return bTs - aTs

    return (b.position ?? 0) - (a.position ?? 0)
  })[0] ?? null

export const pickAnnouncementDisplayStep = (steps: ContactOrderStep[] = []) =>
  pickNextAnnouncementStep(steps) ?? pickLatestStep(steps)

const closedOrderResultStep = (order?: ContactOrder | null) => {
  const steps = Array.isArray(order?.steps) ? order.steps : []
  const resultStepId = `${order?.last_result_step_id ?? ""}`.trim()

  if (resultStepId) {
    const resultStep = steps.find((step) => `${step.id ?? ""}`.trim() === resultStepId)

    if (resultStep) return resultStep
  }

  return pickLatestStep(steps)
}

const closedOrderTimestamp = (order?: ContactOrder | null) => {
  const resultStep = closedOrderResultStep(order)

  return (
    order?.last_result_at ??
    resultStep?.result_recorded_at ??
    order?.updated_at ??
    stepDisplayTimestamp(resultStep) ??
    order?.created_at ??
    null
  )
}

const contactOrderDisplayTimestamp = (order: ContactOrder) => {
  if (isClosedContactOrder(order)) return closedOrderTimestamp(order)

  return (
    stepDisplayTimestamp(pickNextAnnouncementStep(order.steps ?? [])) ??
    order.updated_at ??
    order.created_at ??
    null
  )
}

const contactOrderScheduleEventId = (order?: ContactOrder | null) => {
  const direct = normalizeId(order?.schedule_event_id)

  if (direct) return direct

  const refs =
    (order as { product_refs?: Record<string, unknown> | null } | null | undefined)?.product_refs &&
    typeof (order as { product_refs?: Record<string, unknown> | null }).product_refs === "object"
      ? (order as { product_refs?: Record<string, unknown> }).product_refs
      : null

  return normalizeId(refs?.schedule_event_id)
}

export const announcementContactOrderMatchesScheduleEvent = (
  order?: ContactOrder | null,
  event?: ScheduleEvent | null,
) => {
  const eventId = normalizeId(event?.id)
  const eventContactOrderId = normalizeId(event?.contact_order_id)
  const orderId = normalizeId(order?.id)
  const orderScheduleEventId = contactOrderScheduleEventId(order)

  return Boolean(
    (eventContactOrderId && orderId === eventContactOrderId) ||
    (eventId && orderScheduleEventId === eventId),
  )
}

export const pickAnnouncementContactOrder = (contactOrders: ContactOrder[] = []) => {
  const orders = contactOrders.filter((order) => order?.work_order_id)
  const active = orders.filter(isActiveContactOrder)

  if (active.length) {
    return active.slice().sort((a, b) => {
      const aTs = toTimestamp(contactOrderDisplayTimestamp(a))
      const bTs = toTimestamp(contactOrderDisplayTimestamp(b))

      if (aTs !== bTs) return aTs - bTs

      return `${b.id ?? ""}`.localeCompare(`${a.id ?? ""}`)
    })[0]
  }

  return (
    orders
      .filter(isClosedContactOrder)
      .slice()
      .sort((a, b) => {
        const aTs = toTimestamp(contactOrderDisplayTimestamp(a))
        const bTs = toTimestamp(contactOrderDisplayTimestamp(b))

        if (aTs !== bTs) return bTs - aTs

        return `${b.id ?? ""}`.localeCompare(`${a.id ?? ""}`)
      })[0] ?? null
  )
}

export const deriveCurrentAnnouncementContactOrderByWorkOrderId = (
  contactOrders: ContactOrder[],
  relevantScheduleEventByWorkOrderId: Record<string, ScheduleEvent | undefined>,
) => {
  const ordersByWorkOrderId: Record<string, ContactOrder[]> = {}
  contactOrders.forEach((order) => {
    if (!order?.work_order_id) return

    const relevantEvent = relevantScheduleEventByWorkOrderId[order.work_order_id]

    if (!relevantEvent) return

    if (!announcementContactOrderMatchesScheduleEvent(order, relevantEvent)) return

    ordersByWorkOrderId[order.work_order_id] = [
      ...(ordersByWorkOrderId[order.work_order_id] ?? []),
      order,
    ]
  })

  return Object.fromEntries(
    Object.entries(ordersByWorkOrderId)
      .map(([workOrderId, orders]) => [workOrderId, pickAnnouncementContactOrder(orders)] as const)
      .filter((entry): entry is [string, ContactOrder] => Boolean(entry[1])),
  )
}

export const deriveAnnouncementContactOrderByWorkOrderId = (contactOrders: ContactOrder[]) => {
  const ordersByWorkOrderId: Record<string, ContactOrder[]> = {}
  contactOrders.forEach((order) => {
    if (!order?.work_order_id) return

    ordersByWorkOrderId[order.work_order_id] = [
      ...(ordersByWorkOrderId[order.work_order_id] ?? []),
      order,
    ]
  })

  return Object.fromEntries(
    Object.entries(ordersByWorkOrderId)
      .map(([workOrderId, orders]) => [workOrderId, pickAnnouncementContactOrder(orders)] as const)
      .filter((entry): entry is [string, ContactOrder] => Boolean(entry[1])),
  )
}

export const deriveAnnouncementStepsByWorkOrderId = (contactOrders: ContactOrder[]) => {
  const orderByWorkOrderId = deriveAnnouncementContactOrderByWorkOrderId(contactOrders)

  return Object.fromEntries(
    Object.entries(orderByWorkOrderId).map(([workOrderId, order]) => [
      workOrderId,
      Array.isArray(order.steps) ? order.steps : [],
    ]),
  )
}

export const formatNextAnnouncementStepLabel = (step?: ContactOrderStep | null) => {
  if (!step) return ""

  const channelLabel = displayContactChannelLabel(step.channel)

  return channelLabel || "Ankündigungsschritt"
}

const buildActiveStepPresentation = (
  step?: ContactOrderStep | null,
  steps: ContactOrderStep[] = [],
  appointmentContactLabel?: string | null,
): AnnouncementStatusPresentation => {
  if (!step?.status) {
    return {
      definition: {
        value: "NONE",
        label: "Nicht geplant",
        color: "neutral",
        icon: mdiClockOutline,
        description: "Aktuell ist kein Ankündigungsschritt geplant oder aktiv.",
      },
      label: "Nicht geplant",
      subLabel: null,
      tooltip: buildStatusTooltip({
        title: "Ankündigung",
        status: "Nicht geplant",
        details: "Aktuell ist kein Ankündigungsschritt geplant oder aktiv.",
        appointmentContactLabel,
        steps,
      }),
    }
  }

  const statusInfo = contactOrderStepStatusInfo[step.status as ContactStepPhase]
  const channelLabel = displayContactChannelLabel(step.channel)

  if (!statusInfo) {
    return {
      definition: {
        value: step.status,
        label: channelLabel,
        color: "blue",
        description: "Status ist nicht bekannt.",
      },
      label: channelLabel,
      subLabel: formatShortDateTime(stepDisplayTimestamp(step)),
      tooltip: buildStatusTooltip({
        title: "Ankündigung",
        status: "Unbekannt",
        appointmentContactLabel,
        fields: [["Kanal", channelLabel]],
        steps,
      }),
    }
  }

  const timestamp = stepDisplayTimestamp(step)
  const dueLabel = timestamp ? formatDateTime(timestamp) : "–"

  return {
    definition: {
      value: step.status,
      label: channelLabel,
      color: statusInfo.color,
      icon: statusInfo.icon,
      description: statusInfo.description,
    },
    label: channelLabel,
    subLabel: formatShortDateTime(timestamp),
    tooltip: buildStatusTooltip({
      title: "Ankündigung",
      status: statusInfo.label,
      details: statusInfo.description,
      appointmentContactLabel,
      fields: [
        ["Kanal", channelLabel],
        ["Zeitpunkt", dueLabel && dueLabel !== "–" ? dueLabel : null],
      ],
      steps,
    }),
  }
}

const formatStepSummary = (step?: ContactOrderStep | null) => {
  if (!step) return null

  const channelLabel = displayContactChannelLabel(step.channel)
  const statusInfo = contactOrderStepStatusInfo[step.status as ContactStepPhase]
  const resultLabel = displayContactStepResultStatusLabel(step.result_status)

  return [
    channelLabel,
    statusInfo?.label ?? displayContactOrderStepStatusLabel(step.status),
    resultLabel,
  ]
    .filter(Boolean)
    .join(" · ")
}

const completedFromWhatsAppRead = (order: ContactOrder) =>
  normalizeStatus(order.closed_reason) === "COMPLETED" &&
  (order.steps ?? []).some(
    (step) => step.result_status === "SKIPPED" && step.result_meta?.reason_code === "whatsapp_read",
  )

const resolveClosedOrderResult = (order: ContactOrder) => {
  if (completedFromWhatsAppRead(order)) {
    return {
      label: "Abgeschlossen · WhatsApp gelesen",
      color: "forest" as const,
      icon: mdiCheckBold,
      description:
        "Die Terminankündigung wurde per WhatsApp als gelesen gemeldet. Weitere Kontaktaufnahme ist nicht erforderlich.",
    }
  }

  const reason = normalizeStatus(order.closed_reason)
  const result = normalizeStatus(order.last_result_status)

  if (reason === "CANCELED") {
    return {
      label: "Abgebrochen",
      color: "neutral" as const,
      icon: mdiCloseCircleOutline,
      description: "Der Ankündigungsplan wurde abgebrochen.",
    }
  }

  if (reason === "EXHAUSTED" || unsuccessfulContactResults.has(result)) {
    return {
      label: "Erfolglos",
      color: "orange" as const,
      icon: mdiAlertCircleOutline,
      description: "Der Ankündigungsplan wurde erfolglos abgeschlossen.",
    }
  }

  if (reason === "SUPERSEDED") {
    return {
      label: "Ersetzt",
      color: "neutral" as const,
      icon: mdiCalendarClockOutline,
      description: "Der Ankündigungsplan wurde durch einen neueren Plan ersetzt.",
    }
  }

  return {
    label: "Abgeschlossen",
    color: "emerald" as const,
    icon: mdiCheckBold,
    description: "Der Ankündigungsplan ist abgeschlossen.",
  }
}

const closedContactOrderReasonText = (order: ContactOrder) => {
  if (completedFromWhatsAppRead(order)) {
    return "Die Terminankündigung wurde per WhatsApp als gelesen gemeldet. Nicht mehr erforderliche Kontaktschritte wurden beendet; bereits beauftragte Briefe behalten ihren Versandnachweis."
  }

  const structuredDescription = contactOrderClosedReasonDescription(order)

  if (structuredDescription) return `Warum: ${structuredDescription}`

  const reason = normalizeStatus(order.closed_reason)
  const result = normalizeStatus(order.last_result_status)
  const details = `${order.closed_reason_details ?? order.last_result_description ?? ""}`.trim()

  if (reason === "CANCELED") {
    if (details.includes("schedule_event") || details.includes("Termin")) {
      return "Warum: Der zugehörige aktuelle Termin wurde storniert; für einen neuen Termin braucht es einen neuen Ankündigungsplan."
    }

    return "Warum: Der Ankündigungsplan wurde manuell abgebrochen oder durch einen späteren Ablauf beendet."
  }

  if (reason === "SUPERSEDED") {
    return "Warum: Für den aktuellen Termin wurde ein neuerer Ankündigungsplan erstellt."
  }

  if (reason === "EXHAUSTED" || unsuccessfulContactResults.has(result)) {
    return "Warum: Die geplanten Kommunikationsschritte wurden ohne erfolgreiche Rückmeldung abgeschlossen."
  }

  if (details.includes("work_order_print_batch_closed") || details.includes("Brief gedruckt")) {
    return "Warum: Der Terminbrief wurde gedruckt; damit ist der Ankündigungsplan erledigt."
  }

  if (
    reason === "APPOINTMENT_APPROVED" ||
    result === "APPOINTMENT_APPROVED" ||
    result === "APPOINTMENT_SCHEDULED"
  ) {
    return "Warum: Der Termin wurde erfolgreich vereinbart oder zugesagt."
  }

  return "Warum: Der Ankündigungsplan wurde abgeschlossen."
}

const buildClosedContactOrderPresentation = (
  contactOrder: ContactOrder,
  steps: ContactOrderStep[] = [],
  appointmentContactLabel?: string | null,
): AnnouncementStatusPresentation => {
  const result = resolveClosedOrderResult(contactOrder)
  const resultStep = closedOrderResultStep(contactOrder)
  const timestamp = closedOrderTimestamp(contactOrder)
  const timestampLabel = timestamp ? formatDateTime(timestamp) : null
  const details = closedContactOrderReasonText(contactOrder)
  const reasonLabel = contactOrderClosedReasonLabel(contactOrder.closed_reason)
  const actorLabel = contactOrderClosedActorLabel(contactOrder)
  const closedValue =
    normalizeStatus(contactOrder.closed_reason) ||
    normalizeStatus(contactOrder.last_result_status) ||
    "UNKNOWN"

  return {
    definition: {
      value: `CONTACT_ORDER_CLOSED_${closedValue}`,
      label: result.label,
      color: result.color,
      icon: result.icon,
      description: result.description,
    },
    label: result.label,
    subLabel: buildContactOrderClosureSubLabel(contactOrder) ?? formatShortDateTime(timestamp),
    tooltip: buildStatusTooltip({
      title: "Ankündigung",
      status: result.label,
      details,
      appointmentContactLabel,
      fields: [
        ["Grund", reasonLabel],
        ["Ausgelöst durch", actorLabel],
        ["Zeitpunkt", timestampLabel],
        ["Letzter Schritt", formatStepSummary(resultStep)],
      ],
    }),
  }
}

const buildDerivedAnnouncementPresentation = ({
  order,
  statusCode,
  relevantEvent,
  appointmentContactLabel,
}: {
  order: WorkOrder
  statusCode?: WorkOrderViewStatus | null
  relevantEvent?: ScheduleEvent | null
  appointmentContactLabel?: string | null
}): AnnouncementStatusPresentation => {
  const eventLabel = formatDateTime(relevantEvent?.start_at)
  const agreementTimestamp = eventAgreementTimestamp(relevantEvent)
  const agreementLabel = agreementTimestamp ? formatDateTime(agreementTimestamp) : null
  const agreementShortLabel = agreementTimestamp ? formatShortDateTime(agreementTimestamp) : null
  switch (statusCode) {
    case "AGREED":
      return {
        definition: {
          value: "ALREADY_AGREED",
          label: "Erfolgt",
          color: "emerald",
          icon: mdiHandshakeOutline,
          description: "Kein weiterer Kontakt nötig. Die Terminvereinbarung ist erfolgt.",
        },
        label: "Erfolgt",
        subLabel: agreementShortLabel ? `Vereinbart: ${agreementShortLabel}` : null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Erfolgt",
          details: "Kein weiterer Kontakt nötig. Die Terminvereinbarung ist erfolgt.",
          appointmentContactLabel,
          fields: [
            ["Termin", eventLabel && eventLabel !== "–" ? eventLabel : null],
            ["Vereinbart", agreementLabel && agreementLabel !== "–" ? agreementLabel : null],
          ],
        }),
      }
    case "COMPLETED":
      return {
        definition: {
          value: "ALREADY_COMPLETED",
          label: "Erledigt",
          color: "emerald",
          icon: mdiCalendarCheckOutline,
          description: "Kein weiterer Kontakt nötig. Der Auftrag ist bereits erledigt.",
        },
        label: "Erledigt",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Erledigt",
          details: "Kein weiterer Kontakt nötig. Auftrag wurde bereits erledigt.",
          appointmentContactLabel,
          fields: [["Termin", eventLabel && eventLabel !== "–" ? eventLabel : null]],
        }),
      }
    case "FAILED": {
      const reason = `${order.close_reason ?? ""}`.trim()

      return {
        definition: {
          value: "FAILED",
          label: "Beendet",
          color: "rose",
          icon: mdiCloseCircleOutline,
          description: "Kein weiterer Kontakt geplant. Der Auftrag wurde erfolglos beendet.",
        },
        label: "Beendet",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Beendet",
          details: "Kein weiterer Kontakt geplant. Auftrag wurde erfolglos beendet.",
          appointmentContactLabel,
          fields: [["Grund", reason]],
        }),
      }
    }
    case "IN_AGREEMENT":
      return {
        definition: {
          value: "AWAITING_RESPONSE",
          label: "Antwort offen",
          color: "blue",
          icon: mdiHandshakeOutline,
          description:
            "Terminabstimmung läuft, aktuell ist kein separater Ankündigungsschritt offen.",
        },
        label: "Antwort offen",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Antwort offen",
          details: "Terminabstimmung läuft. Aktuell ist kein separater Ankündigungsschritt offen.",
          appointmentContactLabel,
          fields: [["Termin in Abstimmung", eventLabel && eventLabel !== "–" ? eventLabel : null]],
        }),
      }
    case "PLANNED":
      return {
        definition: {
          value: "NO_CONTACT_RUN",
          label: "Kein Plan",
          color: "blue",
          icon: mdiCalendarClockOutline,
          description:
            "Ein Termin ist in Abstimmung, aber aktuell ist kein aktiver Ankündigungsschritt hinterlegt.",
        },
        label: "Kein Plan",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Kein Plan",
          details:
            "Ein Termin ist in Abstimmung, aber aktuell ist kein aktiver Ankündigungsschritt hinterlegt.",
          appointmentContactLabel,
          fields: [["Termin", eventLabel && eventLabel !== "–" ? eventLabel : null]],
        }),
      }
    case "IN_PLANNING":
      return {
        definition: {
          value: "PLANNING_RUNNING",
          label: "In Planung",
          color: "blue",
          icon: mdiClipboardTextClockOutline,
          description: "Planung läuft, aktuell ist kein konkreter Ankündigungsschritt terminiert.",
        },
        label: "In Planung",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "In Planung",
          details: "Aktuell ist kein konkreter Ankündigungsschritt terminiert.",
          appointmentContactLabel,
        }),
      }
    case "NEW":
    default:
      return {
        definition: {
          value: "PLANNING_PENDING",
          label: "Nicht geplant",
          color: "neutral",
          icon: mdiClockOutline,
          description: "Noch kein Termin und kein aktiver Ankündigungsschritt vorhanden.",
        },
        label: "Nicht geplant",
        subLabel: null,
        tooltip: buildStatusTooltip({
          title: "Ankündigung",
          status: "Nicht geplant",
          details: relevantEvent
            ? "Warum: Für den aktuellen Termin ist noch kein aktiver Ankündigungsplan vorhanden."
            : "Warum: Für diesen Auftrag gibt es aktuell keinen aktiven Termin; deshalb wird kein aktueller Ankündigungsplan angezeigt.",
          appointmentContactLabel,
        }),
      }
  }
}

export const buildAnnouncementStatusPresentation = ({
  order,
  contactOrder,
  step,
  steps = [],
  statusCode,
  relevantEvent,
  appointmentContactLabel,
}: {
  order: WorkOrder
  contactOrder?: ContactOrder | null
  step?: ContactOrderStep | null
  steps?: ContactOrderStep[]
  statusCode?: WorkOrderViewStatus | null
  relevantEvent?: ScheduleEvent | null
  appointmentContactLabel?: string | null
}) => {
  if (isClosedContactOrder(contactOrder)) {
    return buildClosedContactOrderPresentation(
      contactOrder,
      steps.length ? steps : (contactOrder.steps ?? []),
      appointmentContactLabel,
    )
  }

  if (step?.status) {
    return buildActiveStepPresentation(step, steps, appointmentContactLabel)
  }

  if (statusCode && ["AGREED", "COMPLETED", "FAILED"].includes(statusCode)) {
    return buildDerivedAnnouncementPresentation({
      order,
      statusCode,
      relevantEvent,
      appointmentContactLabel,
    })
  }

  return buildDerivedAnnouncementPresentation({
    order,
    statusCode,
    relevantEvent,
    appointmentContactLabel,
  })
}
